"use client";

import { useLayoutEffect, useMemo, useRef } from "react";
import {
  classifyExternalHref,
  isDirectOpenProtocol,
  openExternalSafely,
  useExternalLinkConfirmation,
  LinkConfirmDialog,
  type ExternalLinkTarget,
} from "@west-tennessee-consulting/link-preview/react";
import { sanitizeEmailHtml } from "./sanitize.js";
import { buildEmailIframeSrcDoc } from "../email-iframe-document.js";
import { collapseQuotedTrailer, toggleQuoteRegion } from "../quote-collapse.js";

/**
 * Shared, security-hardened email-body renderer for WTC apps.
 *
 * Pipeline: `body` → optional app-specific {@link SafeEmailBodyProps.prepareMarkup}
 * (normalization / plaintext→HTML) → {@link sanitizeEmailHtml} (canonical DOMPurify
 * allow-list) → iframe `srcDoc`. Outbound links are intercepted in the parent
 * realm (srcDoc + `allow-same-origin` = same origin; no `allow-scripts`, so the
 * email's own scripts never run) and routed through a confirmation interstitial
 * that shows the true destination before opening it in a new tab with
 * `noopener,noreferrer`. This defeats obfuscated tracking/redirect links common
 * in marketing and phishing mail.
 *
 * THE INTERSTITIAL ITSELF NOW LIVES IN
 * `@west-tennessee-consulting/link-preview/react` — classification, the safe
 * open, the confirm state and the dialog. It was written here because inbound
 * mail is where it was first needed, which left a general-purpose security
 * control reachable only by rendering an email. Keeping the iframe plumbing
 * here and the link decision there also means the OG-card preview can later
 * render INSIDE the interstitial, which is what the preview plan calls for.
 *
 * What stays here is genuinely email-shaped: building the srcDoc, quoted-reply
 * collapse, height measurement, and binding the capture-phase listener to the
 * iframe document.
 *
 * See `docs/engineering-rules/SAFE_EMAIL_BODY_RENDERER.md` and
 * `docs/engineering-rules/LINK_HANDLING_CONSOLIDATION.md`.
 */

export type SafeEmailBodyVariant = "full" | "snippet";

export interface PrepareMarkupOptions {
  variant: SafeEmailBodyVariant;
  snippetMaxChars: number;
}

export interface SafeEmailBodyProps {
  /** Raw stored/inbound email body (HTML or plaintext). */
  body: string;
  variant?: SafeEmailBodyVariant;
  /** Wrapper class on the outer container. */
  className?: string;
  /** Extra class on the immediate framing wrapper (kept for API stability). */
  proseClassName?: string;
  /** Max chars used when `variant === "snippet"`. */
  snippetMaxChars?: number;
  /**
   * App-specific transform from raw body → UNsanitized display markup, run
   * before {@link sanitizeEmailHtml}. Omit to render the raw body directly
   * (Hub injects its CRM normalization here; the mail client uses the default).
   */
  prepareMarkup?: (body: string, options: PrepareMarkupOptions) => string;
  /**
   * Collapse the quoted-reply / forwarded trailer behind a "show quoted text"
   * toggle (Gmail/Outlook behavior), so a thread rendered as a stack of
   * per-message cards doesn't repeat — and visibly nest — the history inside
   * every later message. Only the DISPLAY collapses; the stored/sent body is
   * untouched. Default `false` (Hub keeps full inline quotes). `"full"` variant
   * only — a `"snippet"` is already a fixed-height preview.
   */
  collapseQuotedReplies?: boolean;
}

function EmailContentIframe({
  srcDoc,
  variant,
  collapseQuotedReplies,
  onLinkIntercept,
}: {
  srcDoc: string;
  variant: SafeEmailBodyVariant;
  collapseQuotedReplies: boolean;
  onLinkIntercept: (link: ExternalLinkTarget) => void;
}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Latest interceptor without re-binding the (stable) listener each render.
  const interceptRef = useRef(onLinkIntercept);
  interceptRef.current = onLinkIntercept;

  useLayoutEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let resizeObserver: ResizeObserver | undefined;
    let boundDoc: Document | null = null;

    const resizeToContent = (): void => {
      if (variant !== "full") return;
      try {
        const doc = iframe.contentDocument;
        if (!doc?.documentElement) return;
        const bodyH = doc.body?.scrollHeight ?? 0;
        const docH = doc.documentElement.scrollHeight;
        iframe.style.height = `${Math.ceil(Math.max(bodyH, docH, 24))}px`;
      } catch {
        /* srcDoc not ready or access restricted — leave default height */
      }
    };

    // Toggle the collapsed quoted trailer. Returns true when it handled the
    // click (so the link path is skipped).
    const handleQuoteToggle = (target: Element | null): boolean => {
      if (!target?.closest?.("[data-quote-toggle]")) return false;
      const doc = iframe.contentDocument;
      if (doc && toggleQuoteRegion(doc) !== null) {
        resizeToContent();
      }
      return true;
    };

    // Capture-phase handler bound on the iframe document. Runs in the parent
    // realm (allowed via srcDoc + allow-same-origin) and fires regardless of
    // allow-scripts, which only gates the frame's OWN scripts. Swallows native
    // navigation and routes through the confirm interstitial.
    const handleClick = (event: Event): void => {
      const target = event.target as Element | null;
      if (handleQuoteToggle(target)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const anchor = target?.closest?.("a");
      if (!anchor) return;
      const rawHref = anchor.getAttribute("href");
      event.preventDefault();
      event.stopPropagation();
      const link = classifyExternalHref(rawHref);
      if (link) {
        interceptRef.current(link);
        return;
      }
      // mailto:/tel: are safe protocol handlers — open without an interstitial.
      // Everything else (relative, fragments, javascript:) is a no-op.
      if (isDirectOpenProtocol(rawHref)) {
        openExternalSafely((rawHref ?? "").trim());
      }
    };

    const onLoad = (): void => {
      try {
        const doc = iframe.contentDocument;
        if (!doc?.body) {
          resizeToContent();
          return;
        }
        doc.addEventListener("click", handleClick, true);
        boundDoc = doc;
        // Collapse before the first measure so the initial height is the
        // collapsed height (no expand-then-shrink flash).
        if (collapseQuotedReplies && variant === "full") {
          collapseQuotedTrailer(doc);
        }
        resizeToContent();
        if (variant === "full" && !resizeObserver && typeof ResizeObserver !== "undefined") {
          resizeObserver = new ResizeObserver(resizeToContent);
          resizeObserver.observe(doc.body);
        }
      } catch {
        /* ignore */
      }
    };

    iframe.addEventListener("load", onLoad);
    queueMicrotask(onLoad);

    return () => {
      iframe.removeEventListener("load", onLoad);
      resizeObserver?.disconnect();
      boundDoc?.removeEventListener("click", handleClick, true);
    };
  }, [srcDoc, variant, collapseQuotedReplies]);

  return (
    <iframe
      ref={iframeRef}
      title="Email body"
      srcDoc={srcDoc}
      sandbox="allow-same-origin"
      className={variant === "snippet" ? "block w-full bg-transparent h-[4.85rem] overflow-hidden" : "block w-full bg-transparent min-h-[1.5rem]"}
      style={{ border: "none", width: "100%" }}
    />
  );
}

export function SafeEmailBody({
  body,
  variant = "full",
  className,
  proseClassName,
  snippetMaxChars = 300,
  prepareMarkup,
  collapseQuotedReplies = false,
}: SafeEmailBodyProps) {
  // Confirm-before-navigate state lives in the shared hook, which also owns the
  // clear-on-confirm so a double click cannot open the destination twice.
  const { pendingLink, requestNavigation, confirm, cancel } = useExternalLinkConfirmation();

  const srcDoc = useMemo(() => {
    if (!body) return "";
    const prepared = prepareMarkup
      ? prepareMarkup(body, { variant, snippetMaxChars })
      : body;
    const sanitized = sanitizeEmailHtml(prepared);
    return sanitized ? buildEmailIframeSrcDoc(sanitized) : "";
  }, [body, variant, snippetMaxChars, prepareMarkup]);

  if (!srcDoc) {
    return null;
  }

  // Appearance-neutral: the caller owns the wrappers (`className` = outer,
  // `proseClassName` = the div framing the iframe). This lets Hub apply its
  // timeline border / snippet gradient-mask and the mail client its own border,
  // each in its own design tokens, while the iframe + sanitize + interstitial
  // stay shared.
  return (
    <div className={className}>
      <div className={proseClassName}>
        <EmailContentIframe
          srcDoc={srcDoc}
          variant={variant}
          collapseQuotedReplies={collapseQuotedReplies}
          onLinkIntercept={requestNavigation}
        />
      </div>
      <LinkConfirmDialog
        link={pendingLink}
        onCancel={cancel}
        onConfirm={confirm}
      />
    </div>
  );
}
